import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Search, Plus, Trash2, Eye, Settings, UserCircle, AlertTriangle } from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { adminApi } from '../../services/api';
import { TenantRecord, TenantDeletionCheck } from '../../types';
import { handleApiError } from '../../utils/errorHandling';
import { useSubscriptionError } from '../../context/SubscriptionErrorContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import CreateTenantModal from '../../components/admin/CreateTenantModal';
import DeleteTenantModal from '../../components/admin/DeleteTenantModal';
import TenantDetailsModal from '../../components/admin/TenantDetailsModal';
import Toast from '../../components/ui/Toast';

const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantRecord | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const { setSubscriptionError } = useSubscriptionError();

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAllTenants();
      setTenants(data);
      setError(null);
    } catch (err) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchTenants();
    setNotification({ type: 'success', message: 'Tenant created successfully!' });
  };

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    setSelectedTenant(null);
    fetchTenants();
    setNotification({ type: 'success', message: 'Tenant deleted successfully!' });
  };

  const handleImpersonate = (tenant: TenantRecord) => {
    // Use the ImpersonationContext to start managing this tenant
    startImpersonation(tenant.tenantId, tenant.name);
    // Close the details modal if open
    setShowDetailsModal(false);
    setSelectedTenant(null);
    // Navigate to the tenant dashboard
    navigate('/dashboard');
  };

  const handleImpersonateById = (tenantId: string) => {
    // Find the tenant by ID to get the name
    const tenant = tenants.find(t => t.tenantId === tenantId);
    if (tenant) {
      handleImpersonate(tenant);
    }
  };

  const handleDeleteFromDetails = (tenant: TenantRecord) => {
    setShowDetailsModal(false);
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.tenantId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold dark:text-white text-gray-900 flex items-center gap-3">
            <Building className="w-8 h-8" />
            Tenant Management
          </h1>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">
            Manage all tenants in the system
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tenant ID or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
      </Card>

      {/* Tenants Table */}
      <Card>
        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700 border-gray-200">
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Tenant ID</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Name</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Plan</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Users</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Created</th>
                <th className="text-right py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <Building className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="dark:text-gray-400 text-gray-600">
                      {searchTerm 
                        ? 'No tenants match your search' 
                        : 'No tenants yet. Create your first tenant to get started.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => (
                  <tr 
                    key={tenant.tenantId}
                    className="border-b dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm dark:text-gray-300 text-gray-700">
                        {tenant.tenantId}
                      </span>
                    </td>
                    <td className="py-3 px-4 dark:text-white text-gray-900 font-medium">
                      {tenant.name}
                    </td>
                    <td className="py-3 px-4 dark:text-gray-300 text-gray-700">
                      {tenant.subscriptionPlan || 'None'}
                    </td>
                    <td className="py-3 px-4 dark:text-gray-300 text-gray-700">
                      {tenant.userCount || 0}
                    </td>
                    <td className="py-3 px-4 dark:text-gray-400 text-gray-600 text-sm">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowDetailsModal(true);
                          }}
                          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 dark:text-gray-400 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleImpersonate(tenant)}
                          className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                          title="Impersonate"
                        >
                          <UserCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          title="Delete Tenant"
                          disabled={tenant.tenantId === 'mentoros-default'}
                        >
                          <Trash2 className={`w-4 h-4 ${
                            tenant.tenantId === 'mentoros-default'
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 dark:text-red-400'
                          }`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t dark:border-gray-700 border-gray-200">
          <p className="text-sm dark:text-gray-400 text-gray-600">
            Showing {filteredTenants.length} of {tenants.length} tenants
          </p>
        </div>
      </Card>

      {/* Modals */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showDeleteModal && selectedTenant && (
        <DeleteTenantModal
          tenant={selectedTenant}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedTenant(null);
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {showDetailsModal && selectedTenant && (
        <TenantDetailsModal
          tenant={selectedTenant}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTenant(null);
          }}
          onImpersonate={handleImpersonateById}
          onDelete={handleDeleteFromDetails}
        />
      )}

      {/* Toast Notification */}
      {notification && (
        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default TenantsPage;
